export interface AiCaptainMenuItem {
  id: number;
  name: string;
  itemUrl?: string;
  price?: number;
  rating?: number;
  estimatedPrepMinutes?: number;
  imageBig?: string;
  description?: string;
  specialItem?: boolean;
  itemType?: string;
  availability?: boolean;
  spiceLevel?: string;
  tags?: string[];
  offerPrice?: number;
  discount?: number;
  recommendedItem?: boolean;
}

/** Resolve a stable menu id from Peppy AI card payloads. */
export function resolveMenuItemId(raw: Record<string, unknown> | AiCaptainMenuItem): number | null {
  const candidate =
    'id' in raw && raw.id != null
      ? raw.id
      : 'itemId' in raw && (raw as Record<string, unknown>).itemId != null
        ? (raw as Record<string, unknown>).itemId
        : null;
  if (candidate == null) return null;
  const id = typeof candidate === 'number' ? candidate : Number(candidate);
  return Number.isFinite(id) && id > 0 ? Math.floor(id) : null;
}

export function normalizeAiCaptainMenuItem(raw: Record<string, unknown>): AiCaptainMenuItem | null {
  const id = resolveMenuItemId(raw);
  if (id == null) return null;

  const prep =
    raw.estimatedPrepMinutes != null
      ? Number(raw.estimatedPrepMinutes)
      : raw.prepMins != null
        ? Number(raw.prepMins)
        : undefined;

  return {
    id,
    name: String(raw.name ?? ''),
    itemUrl: raw.itemUrl ? String(raw.itemUrl) : undefined,
    price: raw.price != null ? Number(raw.price) : undefined,
    rating: raw.rating != null ? Number(raw.rating) : undefined,
    estimatedPrepMinutes: prep,
    imageBig: raw.imageBig ? String(raw.imageBig) : undefined,
    description:
      raw.summary != null
        ? String(raw.summary)
        : raw.description != null
          ? String(raw.description)
          : undefined,
    specialItem: raw.specialItem === true,
    itemType: raw.itemType ? String(raw.itemType) : undefined,
    availability: raw.availability === false ? false : true,
    spiceLevel: raw.spiceLevel != null ? String(raw.spiceLevel) : undefined,
    tags: Array.isArray(raw.tags)
      ? (raw.tags as unknown[]).map(String).filter((t) => t.trim().length > 0)
      : undefined,
    offerPrice: raw.offerPrice != null ? Number(raw.offerPrice) : undefined,
    discount: raw.discount != null ? Number(raw.discount) : undefined,
    recommendedItem: raw.recommendedItem === true,
  };
}

/** Strip duplicated menu metadata from assistant prose when cards are shown. */
export function extractMenuIntroText(text: string, menuItemCount = 0): string {
  let trimmed = text.trim();
  if (!trimmed) return '';

  const numberedListStart = trimmed.search(/\n\s*1\.\s+/);
  if (numberedListStart > 0) {
    trimmed = trimmed.slice(0, numberedListStart).trim();
  } else if (/^\d+\.\s+/.test(trimmed)) {
    return '';
  }

  const detailBlockStart = trimmed.search(
    /\n(?:Here(?:'s| are)|[-*]\s+\*\*(?:Price|Rating|Estimated|Image|Prep)|!\[)/i
  );
  if (detailBlockStart > 0) {
    trimmed = trimmed.slice(0, detailBlockStart).trim();
  }

  trimmed = trimmed.replace(/\*\*([^*]+)\*\*/g, '$1');
  trimmed = trimmed.replace(/\s+priced at\s+₹[\d,]+/gi, '');
  trimmed = trimmed.replace(/\s+and has a rating of\s+[\d.]+/gi, '');
  trimmed = trimmed.replace(/!\[[^\]]*\]\([^)]+\)/g, '');
  trimmed = trimmed.replace(/\[View Details\]\([^)]+\)/gi, '');

  if (menuItemCount === 1) {
    const questionStart = trimmed.search(/\n\s*Would you like/i);
    if (questionStart > 0) {
      trimmed = trimmed.slice(0, questionStart).trim();
    }
  }

  return trimmed.trim();
}

export function formatCaptainPrice(price?: number): string {
  if (price == null || Number.isNaN(price)) return '';
  return `₹${Math.round(price)}`;
}
